import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import SystemHealthDashboard from './components/SystemHealthDashboard';
import BrokenAuthStatesPanel from './components/BrokenAuthStatesPanel';
import CaptchaManagementPanel from './components/CaptchaManagementPanel';
import SupabaseConfigPanel from './components/SupabaseConfigPanel';
import FailedUserCreationsPanel from './components/FailedUserCreationsPanel';
import AuditLogPanel from './components/AuditLogPanel';
import authRecoveryService from '../../services/authRecoveryService';

const AuthenticationRecoverySystemRepairCenter = () => {
  const [activeTab, setActiveTab] = useState('auth-states');
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    loadSystemHealth();
  }, [refreshTrigger]);

  const loadSystemHealth = async () => {
    try {
      setLoading(true);
      const data = await authRecoveryService?.getSystemHealthDashboard();
      setSystemHealth(data);
    } catch (error) {
      console.error('Error loading system health:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    {
      id: 'auth-states',
      label: 'Auth States',
      icon: 'UserX',
      badge: systemHealth?.brokenAuthStates || 0,
      badgeColor: systemHealth?.brokenAuthStates > 0 ? 'bg-red-500' : 'bg-gray-400'
    },
    {
      id: 'captcha',
      label: 'CAPTCHA',
      icon: 'Shield',
      badge: systemHealth?.captchaFailures24h || 0,
      badgeColor: systemHealth?.captchaFailures24h > 10 ? 'bg-amber-500' : 'bg-gray-400'
    },
    {
      id: 'supabase',
      label: 'Supabase Config',
      icon: 'Database',
      badge: systemHealth?.configIssues || 0,
      badgeColor: systemHealth?.configIssues > 0 ? 'bg-red-500' : 'bg-green-500'
    },
    {
      id: 'user-creation',
      label: 'User Creation',
      icon: 'UserPlus',
      badge: systemHealth?.failedUserCreations || 0,
      badgeColor: systemHealth?.failedUserCreations > 0 ? 'bg-amber-500' : 'bg-gray-400'
    },
    {
      id: 'audit',
      label: 'Audit Log',
      icon: 'FileText',
      badge: null
    }
  ];

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-amber-600 dark:text-amber-400';
      case 'critical':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Icon name="Wrench" size={32} className="text-emerald-600 dark:text-emerald-400" />
                Authentication Recovery & System Repair Center
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Diagnose and resolve authentication failures, broken user states, and system configuration issues
              </p>
            </div>
            <div className="flex items-center gap-3">
              {systemHealth && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${
                  systemHealth?.overallHealth === 'healthy' ?'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : systemHealth?.overallHealth === 'critical' ?'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                  <Icon 
                    name={systemHealth?.overallHealth === 'healthy' ? 'CheckCircle' : 'AlertTriangle'} 
                    size={20} 
                    className={getHealthStatusColor(systemHealth?.overallHealth)}
                  />
                  <span className={`text-sm font-semibold ${getHealthStatusColor(systemHealth?.overallHealth)}`}>
                    {systemHealth?.overallHealth?.toUpperCase()}
                  </span>
                </div>
              )}
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Icon name="RefreshCw" size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* 12-Column Grid Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* System Health Dashboard - 4 columns */}
          <div className="col-span-12 lg:col-span-4">
            <SystemHealthDashboard 
              systemHealth={systemHealth} 
              loading={loading}
              onRefresh={handleRefresh}
            />
          </div>

          {/* Main Repair Panel - 8 columns */}
          <div className="col-span-12 lg:col-span-8">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
              {/* Tabs */}
              <div className="border-b border-gray-200 dark:border-slate-700">
                <div className="flex overflow-x-auto">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'border-emerald-600 text-emerald-600 dark:text-emerald-400' :'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      {tab?.label}
                      {tab?.badge !== null && (
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full text-white ${tab?.badgeColor}`}>
                          {tab?.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'auth-states' && (
                  <BrokenAuthStatesPanel 
                    refreshTrigger={refreshTrigger}
                    onRepairComplete={handleRefresh}
                  />
                )}
                {activeTab === 'captcha' && (
                  <CaptchaManagementPanel 
                    refreshTrigger={refreshTrigger}
                    onResetComplete={handleRefresh}
                  />
                )}
                {activeTab === 'supabase' && (
                  <SupabaseConfigPanel 
                    refreshTrigger={refreshTrigger}
                    onValidationComplete={handleRefresh}
                  />
                )}
                {activeTab === 'user-creation' && (
                  <FailedUserCreationsPanel 
                    refreshTrigger={refreshTrigger}
                    onRetryComplete={handleRefresh}
                  />
                )}
                {activeTab === 'audit' && (
                  <AuditLogPanel refreshTrigger={refreshTrigger} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationRecoverySystemRepairCenter;